import type { IWorklistStore } from '@/Pages/GenericWorklist/Stores/WorklistStore';
import * as API from '@/API';
import { OKResponse } from '@/API';
import { Label } from '@/Models/GenericWorklist/Label';
import LabelMutationErrorResponse from '@/Models/GenericWorklist/LabelMutationErrorResponse';

export interface IWorklistSelectionHelper {
    readonly count: number;
    readonly anySelected: boolean;
    readonly allSelected: boolean | 'indeterminate';
    readonly labelsAssignedToSelectedOrders: Label[];

    toggleSelection(id: number): void;
    isSelected(id: number): boolean;
    clear(): void;
    toggleSelectAll(): void;
    handleMarkAsReady(): Promise<void>;
    assignLabelToOrders(labelId: number): Promise<void>;
    removeLabelFromOrders(labelId: number): Promise<void>;
    isLabelApplied(labelId: number, orderId: number): boolean;
}

export class WorklistSelectionHelper implements IWorklistSelectionHelper {
    private readonly worklistStore: IWorklistStore;

    constructor(worklistStore: IWorklistStore) {
        this.worklistStore = worklistStore;
    }

    toggleSelection = (id: number) => {
        const selectedIds = [...this.worklistStore.selectedIds];

        const index = selectedIds.indexOf(id);
        if (index > -1) {
            selectedIds.splice(index, 1);
        }
        else {
            selectedIds.push(id);
        }

        this.worklistStore.setSelection(selectedIds);
    };

    isSelected(id: number): boolean {
        return this.worklistStore.selectedIds.includes(id);
    }

    clear() {
        this.worklistStore.clearSelection();
    }

    get count() {
        return this.worklistStore.selectedIds.length;
    }

    toggleSelectAll = () => {
        if (this.allSelected === true) {
            this.worklistStore.clearSelection();
            return;
        }

        this.worklistStore.setSelection(this.worklistStore.orders.map((order) => order.OrderInfo.OrderID));
    };

    get anySelected(): boolean {
        return this.worklistStore.selectedIds.length > 0;
    }

    get allSelected(): boolean | 'indeterminate' {
        if (this.worklistStore.selectedIds.length === 0) {
            return false;
        }

        const selectedIDs = new Set(this.worklistStore.selectedIds);
        const currentResultsIDs = new Set(this.worklistStore.orders.map((order) => order.OrderInfo.OrderID));

        const intersection = [...currentResultsIDs].filter((id) => selectedIDs.has(id));

        // If intersection equals both sets, all are selected
        if (intersection.length === currentResultsIDs.size && intersection.length === selectedIDs.size) {
            return true;
        }

        return 'indeterminate';
    }

    handleMarkAsReady = async () => {
        if (this.worklistStore.selectedIds.length === 0) {
            return;
        }
        if (await API.orders.markAsReady(this.worklistStore.selectedIds)) {
            await this.worklistStore.refetchResults();
            this.worklistStore.clearSelection();
        }
    };

    assignLabelToOrders = async (labelId: number) => {
        if (this.worklistStore.selectedIds.length === 0) {
            return;
        }
        await API.labels.assignLabelToOrders<OKResponse, LabelMutationErrorResponse>(
            { labelId, orderIds: this.worklistStore.selectedIds },
            () => {
                void this.worklistStore.refetchResults();
                this.worklistStore.clearSelection();
            },
            (error: LabelMutationErrorResponse) => {
                console.error('Failed to assign label:', error);
            },
        );
    };

    removeLabelFromOrders = async (labelId: number) => {
        if (this.worklistStore.selectedIds.length === 0) {
            return;
        }
        await API.labels.unassignLabelFromOrders<OKResponse, LabelMutationErrorResponse>(
            { labelId, orderIds: this.worklistStore.selectedIds },
            () => {
                void this.worklistStore.refetchResults();
                this.worklistStore.clearSelection();
            },
            (error: LabelMutationErrorResponse) => {
                console.error('Failed to unassign label:', error);
            },
        );
    };

    isLabelApplied = (labelId: number, orderId: number): boolean => {
        const freshOrder = this.worklistStore.getOrderById(orderId);

        if (!freshOrder?.OrderLabelsInfos) {
            return false;
        }

        return freshOrder.OrderLabelsInfos.some((label) => label.ID === labelId);
    };

    get labelsAssignedToSelectedOrders(): Label[] {
        const selectedOrderIds = Array.from(this.worklistStore.selectedIds);

        if (selectedOrderIds.length === 0) {
            return [];
        }

        const selectedOrders = this.worklistStore.orders.filter((order) => selectedOrderIds.includes(order.OrderInfo.OrderID));

        const labelIdSet = new Set<number>();
        selectedOrders.forEach((order) => {
            order.OrderLabelsInfos?.forEach((label) => {
                if (label?.ID) {
                    labelIdSet.add(label.ID);
                }
            });
        });
        return this.worklistStore.availableLabels.filter((label) => labelIdSet.has(label.ID));
    }
}
